// src/components/Announcements/AnnouncementTable.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAnnouncementsTableViewThunk,
  createAnnouncementThunk,
  updateAnnouncementThunk,
  deleteAnnouncementThunk,
} from '../features/announcements/announcementThunks';
import { setFilters, clearFilters } from '../features/announcements/announcementSlice';
import { isDeptHeadOrAbove } from '../RoleBasedRoute';

const AnnouncementTable = () => {
  const dispatch = useDispatch();
  const { tableView, filters, loading, error } = useSelector((state) => state.announcements);
  const { stores } = useSelector((state) => state.stores);
  const { user } = useSelector((state) => state.auth);
  const isAdmin = isDeptHeadOrAbove(user); // rank >= 9 (dept heads + ADMIN)

  // Local state for modal and form
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    store: '',
    announcement_date: '',
    announcement_time: '',
  });

  // State for message modal
  const [messageModal, setMessageModal] = useState({
    show: false,
    type: '', // 'success' or 'error'
    message: ''
  });

  // State for toggling expanded rows (by store_id-date key)
  const [expandedRows, setExpandedRows] = useState({});
  // Admin-only: include inactive stores in table view
  const [includeInactive, setIncludeInactive] = useState(false);

  // Fetch table view on mount and when filters change
  useEffect(() => {
    dispatch(fetchAnnouncementsTableViewThunk(filters));
  }, [dispatch, filters]);

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...filters, [filterName]: value || null };
    dispatch(setFilters(newFilters));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  // Open modal for creating (with optional pre-filled store)
  const handleOpenCreateModal = (storeId = '') => {
    setFormData({
      store: storeId,
      announcement_date: new Date().toISOString().split('T')[0], // Today's date
      announcement_time: new Date().toTimeString().split(' ')[0].slice(0, 5), // Current time HH:MM
    });
    setEditingId(null);
    setShowModal(true);
  };

  // Open modal for editing
  const handleEdit = (announcement, storeId) => {
    setFormData({
      store: storeId,
      announcement_date: announcement.announcement_date,
      announcement_time: announcement.time,
    });
    setEditingId(announcement.id);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ store: '', announcement_date: '', announcement_time: '' });
  };

  // Show message modal
  const showMessage = (type, message) => {
    setMessageModal({ show: true, type, message });
  };

  // Close message modal
  const closeMessageModal = () => {
    setMessageModal({ show: false, type: '', message: '' });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await dispatch(
          updateAnnouncementThunk({
            id: editingId,
            data: formData,
          })
        ).unwrap();
        showMessage('success', 'Announcement updated successfully!');
      } else {
        await dispatch(createAnnouncementThunk(formData)).unwrap();
        showMessage('success', 'Announcement created successfully!');
      }

      handleCloseModal();
      dispatch(fetchAnnouncementsTableViewThunk(filters));
    } catch (err) {
      showMessage('error', `Failed to ${editingId ? 'update' : 'create'} announcement: ${JSON.stringify(err)}`);
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await dispatch(deleteAnnouncementThunk(id)).unwrap();
      showMessage('success', 'Announcement deleted successfully!');
      dispatch(fetchAnnouncementsTableViewThunk(filters));
    } catch (err) {
      showMessage('error', 'Failed to delete announcement: ' + JSON.stringify(err));
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowKey) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };

  // Format date/time in PST
  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get store name by ID
  const getStoreName = (storeId) => {
    const store = stores.find((s) => s.id === parseInt(storeId));
    return store ? store.store_name : 'Unknown Store';
  };

  return (
    <>
      {/* Inject styles */}
      <style>{styles}</style>

      <div className="announcement-table-container">
        {/* Header with Create Button */}
        <div className="announcement-table-header">
          <h2>Store Announcements</h2>
          <button onClick={() => handleOpenCreateModal()} className="announcement-btn-primary">
            + Create Announcement
          </button>
        </div>

        {/* Filters */}
        <div className="announcement-table-filters">
          <div className="announcement-filter-group">
            <label>Store:</label>
            <select
              value={filters.store || ''}
              onChange={(e) => handleFilterChange('store', e.target.value)}
            >
              <option value="">All Stores</option>
              {stores
                .filter(store => (isAdmin && includeInactive) || store.is_active !== false)
                .map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.store_name}{isAdmin && store.is_active === false ? ' (Inactive)' : ''}
                  </option>
                ))}
            </select>
          </div>

          <div className="announcement-filter-group">
            <label>From Date:</label>
            <input
              type="date"
              value={filters.start_date || ''}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </div>

          <div className="announcement-filter-group">
            <label>To Date:</label>
            <input
              type="date"
              value={filters.end_date || ''}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>

              {/* Admin-only filter: include inactive stores */}
              {isAdmin && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#f59e0b', fontWeight: 500, alignSelf: 'flex-end', paddingBottom: '2px' }}>
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                  Include inactive stores
                </label>
              )}

              <button onClick={handleClearFilters} className="announcement-btn-link">
                Clear Filters
              </button>
        </div>

        {/* Loading/Error States */}
        {tableView.loading && <div className="announcement-loading">Loading announcements...</div>}
        {tableView.error && <div className="announcement-error">Error: {JSON.stringify(tableView.error)}</div>}

        {/* Table */}
        <div className="announcement-table-wrapper">
          <table className="announcement-table">
            <thead>
              <tr>
                <th>Store</th>
                <th>Date</th>
                <th>Announcements</th>
                <th>Count</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!tableView.loading && tableView.data.length === 0 && (
                <tr>
                  <td colSpan="5" className="announcement-empty-state">
                    No announcements found. Click "Add" to create one.
                  </td>
                </tr>
              )}

              {tableView.data
                .filter(row => (isAdmin && includeInactive) || row.is_active !== false)
                .map((row) => {
                const rowKey = `${row.store_id}-${row.date}`;
                const isExpanded = expandedRows[rowKey];
                
                // Sort announcements by time in chronological order
                const sortedAnnouncements = [...row.announcements].sort((a, b) => {
                  return a.time.localeCompare(b.time);
                });
                
                const showToggle = sortedAnnouncements.length > 3;
                const displayedAnnouncements = showToggle && !isExpanded 
                  ? sortedAnnouncements.slice(0, 3) 
                  : sortedAnnouncements;

                return (
                  <tr key={rowKey}>
                    <td className="announcement-store-name">
                      {row.store_name}
                      {isAdmin && row.is_active === false && (
                        <span style={{
                          display: 'inline-block',
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: '#dc3545',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '4px',
                          padding: '1px 6px',
                          fontWeight: 500,
                          verticalAlign: 'middle',
                        }}>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>{formatDate(row.date)}</td>
                    <td className="announcement-announcements-cell">
                      <div className="announcement-announcements-list">
                        {displayedAnnouncements.map((announcement) => (
                          <div key={announcement.id} className="announcement-item">
                            <span className="announcement-employee-name">
                              {announcement.announced_by_name}
                            </span>
                            <span className="announcement-time">{formatTime(announcement.time)}</span>
                            
                            {announcement.can_edit && (
                              <div className="announcement-actions">
                                <button
                                  onClick={() => handleEdit(announcement, row.store_id)}
                                  className="announcement-btn-icon"
                                  title="Edit"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(announcement.id)}
                                  className="announcement-btn-icon announcement-btn-icon-delete"
                                  title="Delete"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                    <line x1="10" y1="11" x2="10" y2="17"/>
                                    <line x1="14" y1="11" x2="14" y2="17"/>
                                  </svg>
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Toggle button for expand/collapse */}
                        {showToggle && (
                          <button
                            onClick={() => toggleRowExpansion(rowKey)}
                            className="announcement-btn-toggle-inline"
                          >
                            <span className="announcement-toggle-arrow">{isExpanded ? '▲' : '▼'}</span>
                            {isExpanded 
                              ? `Show Less` 
                              : `${sortedAnnouncements.length - 3} More`}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="announcement-count-cell">{row.count}</td>
                    <td className="announcement-actions-cell">
                      {/* Add button — hidden for inactive stores */}
                      {row.is_active !== false ? (
                        <button
                          onClick={() => handleOpenCreateModal(row.store_id)}
                          className="announcement-btn-add-store"
                          title={`Add announcement for ${row.store_name}`}
                        >
                          + Add
                        </button>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>
                          Inactive
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for Create/Edit */}
      {showModal && (
        <div className="announcement-modal-backdrop" onClick={handleCloseModal}>
          <div className="announcement-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="announcement-modal-header">
              <h3>{editingId ? 'Edit Announcement' : 'Create Announcement'}</h3>
              <button className="announcement-modal-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="announcement-modal-body">
              <div className="announcement-form-group">
                <label>Store *</label>
                <select
                  value={formData.store}
                  onChange={(e) => setFormData({ ...formData, store: e.target.value })}
                  required
                  disabled={!!formData.store} // Disable if pre-filled from store row
                >
                  <option value="">Select Store</option>
                  {stores.filter(store => store.is_active !== false).map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.store_name}
                    </option>
                  ))}
                </select>
                {formData.store && (
                  <small className="announcement-help-text">
                    Selected: {getStoreName(formData.store)}
                  </small>
                )}
              </div>

              <div className="announcement-form-row">
                <div className="announcement-form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.announcement_date}
                    onChange={(e) =>
                      setFormData({ ...formData, announcement_date: e.target.value })
                    }
                    required
                  />
                </div>

                <div className="announcement-form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={formData.announcement_time}
                    onChange={(e) =>
                      setFormData({ ...formData, announcement_time: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="announcement-modal-footer">
                <button type="button" onClick={handleCloseModal} className="announcement-btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="announcement-btn-primary">
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {messageModal.show && (
        <div className="announcement-modal-backdrop" onClick={closeMessageModal}>
          <div className="announcement-message-modal" onClick={(e) => e.stopPropagation()}>
            <div className={`announcement-message-modal-content ${messageModal.type === 'success' ? 'announcement-message-success' : 'announcement-message-error'}`}>
              <div className="announcement-message-icon">
                {messageModal.type === 'success' ? (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                ) : (
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
              </div>
              <h3 className="announcement-message-title">
                {messageModal.type === 'success' ? 'Success!' : 'Error'}
              </h3>
              <p className="announcement-message-text">{messageModal.message}</p>
              <button onClick={closeMessageModal} className="announcement-btn-primary announcement-message-btn">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Comprehensive Styles
const styles = `
/* ===========================
   CONTAINER & LAYOUT
   =========================== */
.announcement-table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  padding: 24px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Header */
.announcement-table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e9ecef;
}

.announcement-table-header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #212529;
}

/* Filters */
.announcement-table-filters {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.announcement-filter-group {
  display: flex;
  flex-direction: column;
  min-width: 200px;
}

.announcement-filter-group label {
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #495057;
}

.announcement-filter-group select,
.announcement-filter-group input {
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s;
}

.announcement-filter-group select:focus,
.announcement-filter-group input:focus {
  outline: none;
  border-color: #f43f5e;
  box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.1);
}

/* ===========================
   TABLE STYLES
   =========================== */
.announcement-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.announcement-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.announcement-table thead {
  background: #4a5568;
  color: white;
}

.announcement-table th {
  padding: 16px 14px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #2d3748;
}

.announcement-table tbody tr {
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.announcement-table tbody tr:hover {
  background-color: #f8f9fa;
}

.announcement-table tbody tr:last-child {
  border-bottom: none;
}

.announcement-table td {
  padding: 16px 14px;
  font-size: 14px;
  color: #495057;
  vertical-align: top;
}

/* Specific cells */
.announcement-store-name {
  font-weight: 600;
  color: #212529;
}

.announcement-count-cell {
  text-align: center;
  font-weight: 600;
  color: #f43f5e;
  font-size: 16px;
}

.announcement-actions-cell {
  text-align: center;
}

.announcement-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-style: italic;
}

/* ===========================
   ANNOUNCEMENTS LIST
   =========================== */
.announcement-announcements-cell {
  max-width: 500px;
}

.announcement-announcements-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.announcement-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #f43f5e;
  transition: all 0.2s;
}

.announcement-item:hover {
  background: #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.announcement-employee-name {
  font-weight: 600;
  color: #212529;
  flex: 1;
}

.announcement-time {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
  white-space: nowrap;
}

.announcement-actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
}

/* Toggle button */
.announcement-btn-toggle-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  margin-top: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #f43f5e;
  transition: all 0.2s;
  font-family: inherit;
}

.announcement-btn-toggle-inline:hover {
  color: #e11d48;
  text-decoration: underline;
}

.announcement-toggle-arrow {
  font-size: 10px;
  display: inline-flex;
  align-items: center;
}

/* ===========================
   BUTTONS
   =========================== */
.announcement-btn-primary,
.announcement-btn-secondary,
.announcement-btn-link,
.announcement-btn-add-store {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: inherit;
}

.announcement-btn-primary {
  background: #f43f5e;
  color: white;
  box-shadow: 0 2px 4px rgba(244, 63, 94, 0.2);
}

.announcement-btn-primary:hover {
  background: #e11d48;
  box-shadow: 0 4px 8px rgba(244, 63, 94, 0.3);
  transform: translateY(-1px);
}

.announcement-btn-secondary {
  background: #6c757d;
  color: white;
}

.announcement-btn-secondary:hover {
  background: #545b62;
}

.announcement-btn-link {
  background: transparent;
  color: #f43f5e;
  text-decoration: none;
  padding: 8px 0;
  font-weight: 500;
}

.announcement-btn-link:hover {
  text-decoration: underline;
  color: #e11d48;
}

.announcement-btn-add-store {
  background: #28a745;
  color: white;
  padding: 8px 16px;
  font-size: 13px;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.2);
}

.announcement-btn-add-store:hover {
  background: #218838;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
  transform: translateY(-1px);
}

.announcement-btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  transition: all 0.2s;
  color: #6c757d;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.announcement-btn-icon:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #007bff;
}

.announcement-btn-icon-delete:hover {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.announcement-btn-icon svg {
  display: block;
}

/* ===========================
   MODAL STYLES
   =========================== */
.announcement-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: announcementFadeIn 0.2s ease;
}

@keyframes announcementFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.announcement-modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: announcementSlideUp 0.3s ease;
}

@keyframes announcementSlideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.announcement-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #dee2e6;
}

.announcement-modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #212529;
}

.announcement-modal-close {
  background: none;
  border: none;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.announcement-modal-close:hover {
  background: #f8f9fa;
  color: #212529;
}

.announcement-modal-body {
  padding: 24px;
}

.announcement-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  margin-top: 20px;
}

/* ===========================
   FORM STYLES
   =========================== */
.announcement-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.announcement-form-group {
  margin-bottom: 16px;
}

.announcement-form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 14px;
  color: #495057;
}

.announcement-form-group select,
.announcement-form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.announcement-form-group select:focus,
.announcement-form-group input:focus {
  outline: none;
  border-color: #f43f5e;
  box-shadow: 0 0 0 3px rgba(244, 63, 94, 0.1);
}

.announcement-form-group select:disabled {
  background: #e9ecef;
  cursor: not-allowed;
}

.announcement-help-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
}

/* ===========================
   LOADING & ERROR STATES
   =========================== */
.announcement-loading,
.announcement-error {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 15px;
}

.announcement-error {
  color: #dc3545;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
}

/* ===========================
   MESSAGE MODAL
   =========================== */
.announcement-message-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  animation: announcementSlideUp 0.3s ease;
}

.announcement-message-modal-content {
  padding: 32px 24px;
  text-align: center;
}

.announcement-message-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.announcement-message-success .announcement-message-icon {
  color: #28a745;
}

.announcement-message-error .announcement-message-icon {
  color: #dc3545;
}

.announcement-message-title {
  margin: 0 0 12px 0;
  font-size: 22px;
  font-weight: 600;
  color: #212529;
}

.announcement-message-text {
  margin: 0 0 24px 0;
  font-size: 15px;
  color: #6c757d;
  line-height: 1.5;
}

.announcement-message-btn {
  width: 100%;
  max-width: 200px;
}

.announcement-message-success .announcement-btn-primary {
  background: #28a745;
}

.announcement-message-success .announcement-btn-primary:hover {
  background: #218838;
}

/* ===========================
   RESPONSIVE DESIGN
   =========================== */
@media (max-width: 768px) {
  .announcement-table-container {
    padding: 12px;
  }

  .announcement-table-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .announcement-table-filters {
    flex-direction: column;
    align-items: stretch;
  }

  .announcement-filter-group {
    min-width: 100%;
  }

  .announcement-form-row {
    grid-template-columns: 1fr;
  }
  
  .announcement-item {
    flex-wrap: wrap;
  }

  .announcement-actions {
    width: 100%;
    justify-content: flex-end;
    margin-left: 0;
    margin-top: 8px;
  }

  .announcement-modal-content {
    width: 95%;
    margin: 10px;
  }

  .announcement-table th,
  .announcement-table td {
    padding: 10px 8px;
    font-size: 13px;
  }
}
`;

export default AnnouncementTable;